/**
 * German display labels for enums – shared by the admin UI (`StatusChip`), CSV/Sheet exports and
 * emails so that wording is identical everywhere.
 */
import {
  ActivationCodeStatus,
  BatchStatus,
  DocumentStatus,
  DocumentType,
  EmailDeliveryStatus,
  IntegrationJobStatus,
  IntegrationJobType,
  RegistrationStatus,
} from './enums';
import { PublicRegistrationState } from './api/public';

export const ACTIVATION_CODE_STATUS_LABELS = {
  [ActivationCodeStatus.AVAILABLE]: 'Verfügbar',
  [ActivationCodeStatus.PENDING_EMAIL]: 'E-Mail ausstehend',
  [ActivationCodeStatus.CLAIMED]: 'Beansprucht',
  [ActivationCodeStatus.PROVISIONING]: 'In Provisionierung',
  [ActivationCodeStatus.REDEEMED]: 'Aktiviert',
  [ActivationCodeStatus.DISABLED]: 'Deaktiviert',
} as const satisfies Record<ActivationCodeStatus, string>;

export const REGISTRATION_STATUS_LABELS = {
  [RegistrationStatus.PENDING_EMAIL]: 'E-Mail ausstehend',
  [RegistrationStatus.EMAIL_VERIFIED]: 'E-Mail bestätigt',
  [RegistrationStatus.PROVISIONING]: 'In Provisionierung',
  [RegistrationStatus.ACTIVE]: 'Aktiv',
  [RegistrationStatus.PROVISIONING_FAILED]: 'Provisionierung fehlgeschlagen',
  [RegistrationStatus.EXPIRED]: 'Abgelaufen',
  [RegistrationStatus.CANCELLED]: 'Abgebrochen',
} as const satisfies Record<RegistrationStatus, string>;

export const PUBLIC_REGISTRATION_STATE_LABELS = {
  [PublicRegistrationState.PENDING_EMAIL]: 'Bitte bestätigen Sie Ihre E-Mail-Adresse.',
  [PublicRegistrationState.PROVISIONING]: 'Ihr Zugang wird gerade eingerichtet.',
  [PublicRegistrationState.PROVISIONING_DELAYED]:
    'Die Einrichtung verzögert sich. Sie erhalten eine E-Mail, sobald Ihr Zugang bereit ist.',
  [PublicRegistrationState.ACTIVE]: 'Ihr Zugang wurde erfolgreich aktiviert.',
  [PublicRegistrationState.EXPIRED]: 'Die Bestätigungsfrist ist abgelaufen.',
  [PublicRegistrationState.CANCELLED]: 'Diese Aktivierung wurde abgebrochen.',
} as const satisfies Record<PublicRegistrationState, string>;

export const EMAIL_DELIVERY_STATUS_LABELS = {
  [EmailDeliveryStatus.UNKNOWN]: 'Unbekannt',
  [EmailDeliveryStatus.SENT]: 'Versendet',
  [EmailDeliveryStatus.DELIVERED]: 'Zugestellt',
  [EmailDeliveryStatus.BOUNCED]: 'Unzustellbar',
  [EmailDeliveryStatus.COMPLAINED]: 'Als Spam gemeldet',
} as const satisfies Record<EmailDeliveryStatus, string>;

export const BATCH_STATUS_LABELS = {
  [BatchStatus.GENERATING]: 'Wird erzeugt',
  [BatchStatus.READY]: 'Bereit',
  [BatchStatus.DISABLED]: 'Deaktiviert',
} as const satisfies Record<BatchStatus, string>;

export const INTEGRATION_JOB_STATUS_LABELS = {
  [IntegrationJobStatus.PENDING]: 'Wartend',
  [IntegrationJobStatus.DISPATCHED]: 'Eingeplant',
  [IntegrationJobStatus.RUNNING]: 'Läuft',
  [IntegrationJobStatus.SUCCEEDED]: 'Erfolgreich',
  [IntegrationJobStatus.FAILED_RETRYABLE]: 'Fehlgeschlagen (Wiederholung geplant)',
  [IntegrationJobStatus.DEAD]: 'Endgültig fehlgeschlagen',
} as const satisfies Record<IntegrationJobStatus, string>;

export const INTEGRATION_JOB_TYPE_LABELS = {
  [IntegrationJobType.GENERATE_BATCH_CODES]: 'Codes erzeugen',
  [IntegrationJobType.PROVISION_LEARNINGSUITE]: 'LearningSuite-Provisionierung',
  [IntegrationJobType.SEND_VERIFICATION_EMAIL]: 'Bestätigungsmail',
  [IntegrationJobType.SEND_ADMIN_LOGIN_EMAIL]: 'Admin-Anmeldelink',
  [IntegrationJobType.RENDER_BATCH_PDF]: 'Dokumenterstellung (PDF/CSV)',
  [IntegrationJobType.SYNC_BATCH_SHEET]: 'Google-Sheet-Sync',
} as const satisfies Record<IntegrationJobType, string>;

export const DOCUMENT_STATUS_LABELS = {
  [DocumentStatus.PENDING]: 'Wird erstellt',
  [DocumentStatus.READY]: 'Bereit',
  [DocumentStatus.FAILED]: 'Fehlgeschlagen',
} as const satisfies Record<DocumentStatus, string>;

export const DOCUMENT_TYPE_LABELS = {
  [DocumentType.BATCH_PDF]: 'Batch-PDF',
  [DocumentType.BATCH_ZIP]: 'ZIP (Einzel-PDFs)',
  [DocumentType.CODE_PDF]: 'Einzel-PDF',
  [DocumentType.BATCH_CSV]: 'CSV-Export',
} as const satisfies Record<DocumentType, string>;
